import { accessRecoveryFrom } from "../../../../../shared/graphql/accessRecovery";
import { entriesFrom } from "../../../../../shared/graphql/errorEntries";
import {
  ambiguousTransportStatus,
  bookingFailureFrom,
  termsRefusalFrom,
  type BookingFailure,
  type BookingOutcome,
} from "../booking/operations";
import type { Call, CodeRequestAnswer, CodeVerifyAnswer } from "./answers";
import type { FrozenGuestBookingInput } from "./checkout";

/**
 * THE THREE CALLS GUEST CHECKOUT MAKES, as a port.
 *
 * No implementation talks to Backend yet, on purpose: the operations are in
 * the UNPUBLISHED draft `booking-v1@1.11.0`, and this repo does not hand-write
 * GraphQL against an unpublished schema. At cutover one adapter implements
 * this interface from the generated 1.11 operations; until then only tests
 * and the local harness do, with fakes.
 *
 * Every method resolves; none throws. A transport that loses the answer says
 * `lost`, and the machine decides what that means — for `submit`, that the
 * booking MAY exist.
 */
export interface GuestCheckoutPort {
  requestCode(input: { email: string; idempotencyKey: string }): Promise<Call<CodeRequestAnswer>>;
  verifyCode(input: {
    verificationId: string;
    idempotencyKey: string;
    /** Six ASCII digits. Never logged, reported, stored or put in a URL. */
    oneTimeCode: string;
  }): Promise<Call<CodeVerifyAnswer>>;
  /** Sends `input` EXACTLY. The adapter may not add, drop or re-normalise a field. */
  submit(input: FrozenGuestBookingInput): Promise<SubmitResult>;
}

export type SubmitResult =
  /** `managementUrl` is raw and untrusted until `acceptManagementUrl` says otherwise. */
  | { readonly kind: "answered"; readonly outcome: BookingOutcome; readonly managementUrl: unknown }
  | { readonly kind: "refused"; readonly failure: GuestBookingFailure }
  | { readonly kind: "lost" };

/**
 * `createBooking`'s documented refusals, plus the two the draft adds for
 * guests. Both new ones are raised before any write, per the draft's own
 * words; that claim is Backend's and is one of the questions this batch
 * returns (see docs/design/guest-verification-readiness.md).
 */
export type GuestBookingFailure =
  | BookingFailure
  /** FORBIDDEN + GUEST_CONTACT_UNVERIFIED: missing, expired, consumed or mismatched proof. */
  | { kind: "guest-unverified" }
  /** SERVICE_NOT_CONFIGURED + GUEST_BOOKING_UNAVAILABLE: guest checkout is off right now. */
  | { kind: "guest-unavailable" }
  /**
   * IDEMPOTENCY_CONFLICT (409): a booking EXISTS under this key, made by a
   * different request (Backend raises it only from a receipt on file). Never
   * a definite refusal: the machine answers it with the may-have-gone-through
   * state and a Check under the ORIGINAL request (security re-audit, rc.8).
   */
  | { kind: "idempotency-conflict" };

/**
 * THE REPO'S TRANSPORT GATES STILL COME FIRST — with one stated exception.
 *
 * `bookingFailureFrom` treats a code arriving on a 408 or 5xx, or with
 * RETRY_PRESERVING_SESSION, as NOT evidence of a rollback, and so as `lost`.
 * GUEST_CONTACT_UNVERIFIED is held to that rule like every other code.
 *
 * GUEST_BOOKING_UNAVAILABLE is the exception, because the draft sends it AS a
 * 503 (Backend 5ab78f8b, src/utils/errors.ts: SERVICE_NOT_CONFIGURED is
 * 503) and documents it as a pre-write refusal: guest checkout is switched
 * off. Treating it as `lost` would put every guest who hits the kill switch on
 * a "may have gone through" screen for a booking that cannot exist. Whether
 * that pre-write claim holds is readiness question Q-B2/Q-B3, and this
 * exception must be re-checked against the published contract at cutover.
 */
export function guestBookingFailureFrom(error: unknown): GuestBookingFailure {
  const entries = entriesFrom(error);
  const retryPreserving = accessRecoveryFrom(error)?.accessRecovery === "RETRY_PRESERVING_SESSION";
  const ambiguousStatus = ambiguousTransportStatus(error);
  // THE EXCEPTION IS THE 503 IT IS SENT ON, AND NOTHING WIDER. The same code
  // on a 502, a 504 or a 408, or asking to be retried, is a transport answer
  // like any other: the booking may exist. (Reviewer P3.)
  if (
    !retryPreserving &&
    (ambiguousStatus === null || ambiguousStatus === 503) &&
    entries.some(({ code, ext }) => code === "SERVICE_NOT_CONFIGURED" && ext.detailCode === "GUEST_BOOKING_UNAVAILABLE")
  ) {
    return { kind: "guest-unavailable" };
  }
  // A booking exists under this key (see the type). Its 409 is not a
  // transport status; a 408 or 5xx carrying it is `lost`, also uncertain.
  if (
    !retryPreserving &&
    ambiguousStatus === null &&
    entries.some(({ code }) => code === "IDEMPOTENCY_CONFLICT")
  ) {
    return { kind: "idempotency-conflict" };
  }
  // The Terms refusals, with the same one-503 exception for TERMS_UNAVAILABLE
  // (termsRefusalFrom in booking/operations.ts). Read before the transport
  // gate below for that reason; the classifier applies the gate itself.
  const terms = termsRefusalFrom(error);
  if (terms !== null) return { kind: "terms", refusal: terms };
  if (retryPreserving) return { kind: "lost" };
  if (ambiguousStatus !== null) return { kind: "lost" };
  if (entries.some(({ code, ext }) => code === "FORBIDDEN" && ext.detailCode === "GUEST_CONTACT_UNVERIFIED")) {
    return { kind: "guest-unverified" };
  }
  return bookingFailureFrom(error);
}
